// @flow
import classnames from 'classnames';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MagnifyingGlassIcon from '../../../../assets/icons/magnifying-glass.svg';

import { AVA_DOMAIN } from '../../../constants';
import UserAvatar from '../../Avatars/UserAvatar';

import { users as Users } from '../../../services/api/ava';

import type { JoiningStatus } from '../../../types/conversation';
import type { Notification } from '../../../types/cc-mode';

import { getSnack } from '../../../utils/snackbar';
import { formatAvaName, isAvaRoomValid } from '../../../utils/search-ava-room';
import { getSocketIfExist } from '../../../utils/getSocket';
import { log } from '../../../utils/log';
import * as segment from '../../../utils/segment';
import { sendConnectionRequestMessage, sendUserQueryMessage } from '../../../utils/ws-v1';

import useStyles from './SearchAvaRoomInput.styles';

interface SuggestedUser {
  userName: string;
  avaName: string;
  userPhoto?: {
    url: string,
  };
}

interface Props {
  addNotification: (notification: Notification) => void;
  electronCaptionMode: boolean;
  history: Object;
  joiningStatus: JoiningStatus;
  setInviteCardOpen: (isOpen: boolean) => void;
  setJoiningStatus: (joiningStatus: JoiningStatus) => void;
  setLoadingApp: (isLoading: boolean) => void;
  user: Object;
}

const SearchAvaRoomInput = ({
  addNotification,
  electronCaptionMode,
  setInviteCardOpen,
  setJoiningStatus,
  setLoadingApp,
  user,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  const [avaNameInput, setAvaNameInput] = useState('');
  const [suggestedUsers, setSuggestedUsers] = useState<SuggestedUser[]>([]);
  const [inputFocused, setInputFocused] = useState(false);

  const handleUserQueryResponse = message => {
    const data = JSON.parse(message.data);
    log('websocketMessage', 'info', { message: data, direction: 'b2f' });
    if (data.type === 'user-query-response') {
      setSuggestedUsers(data.users.slice(0, 9));
    }
  };

  useEffect(() => {
    const socket = getSocketIfExist();
    if (socket) {
      socket.addEventListener('message', handleUserQueryResponse);
    }

    return () => {
      if (socket) {
        socket.removeEventListener('message', handleUserQueryResponse);
      }
    };
  }, []);

  const joinAvaRoom = async (avaRoom: string) => {
    if (!isAvaRoomValid(avaRoom)) return;

    setJoiningStatus('requested');
    setInviteCardOpen(false);
    setLoadingApp(true);

    const socket = getSocketIfExist();
    const id = sendConnectionRequestMessage(socket, 'default', { avaName: avaRoom }, {});
    let gotConnectionResponse = false;

    const handleConnectResponse = message => {
      const data = JSON.parse(message.data);

      const stopListening = () => {
        if (socket) {
          socket.removeEventListener('message', handleConnectResponse);
        }
        setLoadingApp(false);
      };

      if (data.type === 'connection-response' && data.id === id) {
        log('websocketMessage', 'info', {
          message: data,
          direction: 'b2m',
        });
        gotConnectionResponse = true;

        // Show the error message if there was a problem connecting to the room.
        if (!data.value) {
          setJoiningStatus('none');
          stopListening();
          gotConnectionResponse = false;
          const errorMessage = t(`snackbar.request.${data.reason || 'unknownError'}`);
          if (electronCaptionMode) {
            addNotification({
              text: errorMessage,
              timestamp: Date.now(),
              type: 'default',
            });
          } else {
            getSnack({ enqueueSnackbar, closeSnackbar })(errorMessage, {
              variant: 'error',
            });
          }
        }
      } else if ((data.type === 'room-status' || data.type === 'room-status-update') && gotConnectionResponse) {
        setJoiningStatus('accepted');
        stopListening();
        segment.track('Joined Conversation', {
          HostAvaName: data.host.avaName,
          HostUserId: data.host.avaId,
          'Join Method': 'Connect Request',
        });
        Users.updateJoinMetrics({
          avaId: user.avaId,
          firebaseAuthUID: user.firebaseAuthUID || user.id,
          joinMetrics: {
            'Total Joins': 1,
            'Total Invites Connected Count': 0,
          },
        });
      }
    };

    if (socket) {
      socket.addEventListener('message', handleConnectResponse);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.top}>
        <span className={classes.avaLink}>web.{AVA_DOMAIN}/</span>
        <div
          className={classnames(classes.inputContainer, {
            [classes.inputContainerFocused]: inputFocused,
          })}
        >
          <div className={classes.iconContainer}>
            <img className={classes.icon} src={MagnifyingGlassIcon} alt="" />
          </div>
          <input
            className={classes.input}
            value={avaNameInput}
            placeholder={t('ccMode.inviteCard.enterRoomName')}
            onChange={event => {
              const formattedAvaName: string = formatAvaName(event.target.value);
              setAvaNameInput(formattedAvaName);
              if (formattedAvaName) {
                const socket = getSocketIfExist();
                if (socket) {
                  sendUserQueryMessage(socket, formattedAvaName);
                }
              }
            }}
            onKeyDown={event => {
              if (['Enter'].includes(event.key)) {
                joinAvaRoom(avaNameInput);
              }
            }}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            autoFocus
          />
        </div>
      </div>
      <div className={classes.bottom}>
        {suggestedUsers.map((user: SuggestedUser) => (
          <div className={classes.card} key={user.avaName} onClick={() => joinAvaRoom(user.avaName)}>
            <div className={classes.avatar}>
              <UserAvatar height={24} width={24} user={user} />
            </div>
            <span className={classes.avaName}>{user.avaName}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SearchAvaRoomInput;
